import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/hooks/use-language";
import { cn } from "@/lib/utils";
import { Chip, gridOverlayClass, type Accent } from "./Section";

export interface HeroStat {
  value: string;
  plus?: boolean;
  label: string;
}

interface PageHeroProps {
  breadcrumb?: { homeHref: string; current: string };
  badge: string;
  title: ReactNode;
  lead: string;
  actions: ReactNode;
  standards?: string[];
  stats: HeroStat[];
  accent?: Accent;
}

/**
 * Mörk hero för start/TGA/TLE: samma uttryck som Hero.tsx (gradient-hero, rutnät, våg)
 * men vänsterställd, med valfri brödsmula, standardchips och nyckeltal.
 */
export function PageHero({ breadcrumb, badge, title, lead, actions, standards, stats, accent = "gold" }: PageHeroProps) {
  const { t } = useLanguage();
  const teal = accent === "teal";

  return (
    <section className="relative overflow-hidden gradient-hero">
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div
          className={cn(
            "absolute -right-32 top-1/4 hidden h-[500px] w-[500px] rounded-full blur-[120px] md:block",
            teal ? "bg-teal/15" : "bg-gold/15",
          )}
        />
        <div className="absolute -left-40 bottom-0 hidden h-[600px] w-[600px] rounded-full bg-navy-light/30 blur-[150px] md:block" />
        <div className={gridOverlayClass} />
      </div>

      <div className="container relative z-10 mx-auto px-6 pb-32 pt-32 lg:px-8 lg:pb-40 lg:pt-40">
        <div className="max-w-4xl">
          {breadcrumb && (
            <nav aria-label={t("Brödsmulor", "Breadcrumb")} className="mb-6 text-sm text-white/50">
              <ol className="flex items-center gap-2">
                <li>
                  <a href={breadcrumb.homeHref} className="transition-colors hover:text-white">
                    {t("Hem", "Home")}
                  </a>
                </li>
                <li aria-hidden>›</li>
                <li aria-current="page" className="text-white/80">
                  {breadcrumb.current}
                </li>
              </ol>
            </nav>
          )}

          <div className="mb-8 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-5 py-2.5 backdrop-blur-sm">
            <span className={cn("h-2 w-2 animate-pulse rounded-full", teal ? "bg-teal" : "bg-gold")} />
            <span className="text-sm font-medium text-white/80">{badge}</span>
          </div>

          <h1 className="mb-6 break-words font-display text-[2rem] font-normal leading-[1.1] text-white sm:text-5xl lg:text-6xl">{title}</h1>
          <p className="mb-10 max-w-2xl text-lg leading-relaxed text-white/65 md:text-xl">{lead}</p>

          <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:flex-wrap">{actions}</div>

          {standards && (
            <div className="mb-12 flex flex-wrap items-center gap-2 text-sm text-white/50">
              <span className="mr-1">{t("Standarder:", "Standards:")}</span>
              {standards.map((s) => (
                <Chip key={s} dark>
                  {s}
                </Chip>
              ))}
            </div>
          )}

          <dl
            className={cn(
              "grid max-w-3xl gap-8 border-t border-white/10 pt-8",
              stats.length === 4 ? "grid-cols-2 md:grid-cols-4" : "grid-cols-3",
            )}
          >
            {stats.map((s) => (
              <div key={s.label} className="flex flex-col-reverse justify-end">
                <dt className="text-sm font-medium text-white/50">{s.label}</dt>
                <dd className="mb-2 font-display text-3xl text-white md:text-4xl">
                  {s.value}
                  {s.plus && <span className={teal ? "text-teal" : "text-gold"}>+</span>}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </div>

      <div className="absolute bottom-[-1px] left-0 right-0 translate-y-px">
        <svg viewBox="0 0 1440 120" preserveAspectRatio="none" fill="none" xmlns="http://www.w3.org/2000/svg" className="block h-[min(8.333vw,120px)] w-full" aria-hidden>
          <path
            d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z"
            fill="hsl(var(--background))"
          />
        </svg>
      </div>
    </section>
  );
}

/**
 * "Boka demo" med samma href och text som övriga sajten (befintliga GTM-triggers) plus
 * data-cta-attribut för klickspårning per placering.
 */
export function DemoButton({ location, size = "xl", className }: { location: string; size?: "lg" | "xl"; className?: string }) {
  const { t, localePath } = useLanguage();
  return (
    <Button asChild variant="hero" size={size} className={className}>
      <Link to={localePath("/book-a-demo")} data-cta="boka-demo" data-cta-location={location}>
        {t("Boka demo", "Book a demo")}
        <ArrowRight className="h-5 w-5" />
      </Link>
    </Button>
  );
}
